import type { ArchivedGroup, QueuedAction, QueuedActionType } from './lib/offline-queue';

const LABELS: Record<QueuedActionType, string> = {
	entrata: 'Inizio giornata',
	uscita: 'Fine giornata',
	riapriGiornata: 'Riapertura giornata',
	entraCantiere: 'Ingresso cantiere',
	esciCantiere: 'Uscita cantiere',
	entraSessioneProvvisoria: 'Ingresso cantiere da associare',
	esciSessioneProvvisoria: 'Uscita cantiere da associare',
};

function downloadSummary(value: unknown, name: string) {
	const url = URL.createObjectURL(
		new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' }),
	);
	const link = document.createElement('a');
	link.href = url;
	link.download = name;
	document.body.appendChild(link);
	link.click();
	link.remove();
	setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function ActionDetails({
	actions,
	ownerId,
	technical = false,
}: {
	actions: QueuedAction[];
	ownerId: string | null;
	technical?: boolean;
}) {
	return (
		<ol className="mt-2 space-y-3">
			{actions.map((action) => (
				<li key={action.id}>
					{action.ownerId && action.ownerId !== ownerId ? (
						<span>Timbratura di un altro accesso</span>
					) : (
						<>
							<strong>{LABELS[action.type]}</strong> —{' '}
							{new Date(action.payload.device_timestamp).toLocaleString('it-IT')}
							{technical ? (
								<p className="break-all">Riferimento: {action.id}</p>
							) : (
								action.lastError && <p className="mt-1 text-red-800">{action.lastError}</p>
							)}
						</>
					)}
				</li>
			))}
		</ol>
	);
}

const ACTION_CLASS =
	'mt-2 flex min-h-11 items-center rounded-lg px-2 py-2 text-left font-semibold underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50';

export function QueueStatus({
	actions,
	archives = [],
	storageError,
	syncing,
	archiving = false,
	isOnline,
	ownerId,
	onRetry,
	onArchive,
}: {
	actions: QueuedAction[];
	archives?: ArchivedGroup[];
	storageError: string | null;
	syncing: boolean;
	archiving?: boolean;
	isOnline: boolean;
	ownerId: string | null;
	onRetry: () => void;
	onArchive?: () => void;
}) {
	if (storageError)
		return (
			<div
				role="alert"
				className="mb-2 rounded-xl border border-red-300 bg-red-50 p-3 text-sm text-red-900"
			>
				{storageError}
			</div>
		);
	const first = actions[0];
	const wrongOwner = !ownerId || !first?.ownerId || first.ownerId !== ownerId;
	const canManage =
		Boolean(ownerId) && actions.every((action) => !action.ownerId || action.ownerId === ownerId);
	const ownArchives = ownerId
		? archives.filter((group) =>
				group.actions.every((action) => !action.ownerId || action.ownerId === ownerId),
			)
		: [];
	const count = actions.length === 1 ? '1 timbratura' : `${actions.length} timbrature`;
	const attempted = actions.some((action) => action.lastAttemptAt || action.lastError);
	const hasOwnError = actions.some(
		(action) => (!action.ownerId || action.ownerId === ownerId) && action.lastError,
	);
	return (
		<>
			{!isOnline && !actions.length && (
				<p role="status" className="mb-2 text-sm text-amber-800">
					Senza connessione. Puoi timbrare: salviamo sul telefono.
				</p>
			)}
			{first && (
				<section
					aria-label="Timbrature in attesa"
					className="mb-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950"
				>
					<p role="status" aria-atomic="true" className="font-bold">
						{archiving
							? 'Archiviazione in corso…'
							: syncing
								? 'Invio in corso…'
								: `${count} ${wrongOwner ? 'da verificare' : attempted ? 'in attesa di conferma' : 'in attesa di invio'}`}
					</p>
					<p className="mt-1">
						Salvate sul telefono.{!wrongOwner && ' Puoi continuare a timbrare.'}
					</p>
					{wrongOwner ? (
						<p role="alert" className="mt-1">
							{first.ownerId
								? 'Rientra con il dipendente che ha salvato queste timbrature.'
								: 'Non è indicato il dipendente. Verifica queste timbrature con il responsabile.'}
						</p>
					) : (
						!isOnline && <p className="mt-1">Riproveremo appena torna la connessione.</p>
					)}
					{hasOwnError && !syncing && !archiving && (
						<p className="mt-1">L’invio non è stato confermato. Trovi il motivo nel riepilogo.</p>
					)}
					<button
						type="button"
						disabled={!isOnline || syncing || archiving || wrongOwner}
						onClick={onRetry}
						className="mt-2 min-h-11 rounded-lg border border-amber-700 px-3 py-2 font-bold focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50"
					>
						Riprova invio
					</button>
					<details className="mt-2">
						<summary className="min-h-11 cursor-pointer py-3 font-semibold">
							Timbrature e problemi
						</summary>
						<ActionDetails actions={actions} ownerId={ownerId} />
						<p className="mt-3">
							Finché manca la conferma, le timbrature successive aspettano. Non cancellare i dati
							dell’app.
						</p>
						<details className="mt-2 border-t border-amber-200 pt-1">
							<summary className="min-h-11 cursor-pointer py-3 font-semibold">
								Assistenza e dettagli tecnici
							</summary>
							<p>Mostra questi riferimenti al responsabile per verificare le timbrature.</p>
							<ActionDetails actions={actions} ownerId={ownerId} technical />
							{canManage && (
								<>
									<button
										type="button"
										className={ACTION_CLASS}
										onClick={() => downloadSummary(actions, 'timbrature-da-verificare.json')}
									>
										Scarica dati per l’assistenza
									</button>
									{onArchive && (
										<button
											type="button"
											disabled={syncing || archiving}
											className={ACTION_CLASS}
											onClick={() => {
												if (
													window.confirm(
														'Hai verificato TUTTE queste timbrature con il responsabile? Verranno archiviate sul telefono senza inviarle. Anche le azioni successive resteranno conservate nell’archivio, consultabile e scaricabile.',
													)
												)
													onArchive();
											}}
										>
											Archivia tutte dopo la verifica
										</button>
									)}
								</>
							)}
						</details>
					</details>
				</section>
			)}
			{ownArchives.length > 0 && (
				<details className="mb-2 rounded-xl border p-3 text-sm">
					<summary className="min-h-11 cursor-pointer py-2 font-semibold">
						Timbrature archiviate sul telefono (
						{ownArchives.reduce((sum, group) => sum + group.actions.length, 0)})
					</summary>
					<p>
						Conservate dopo verifica con il responsabile, senza conferma di invio. Non verranno
						inviate automaticamente.
					</p>
					{ownArchives.map((group) => (
						<div key={group.id} className="mt-3 border-t pt-3">
							<p>Archiviate il {new Date(group.archivedAt).toLocaleString('it-IT')}</p>
							<ActionDetails actions={group.actions} ownerId={ownerId} />
							<details className="mt-2">
								<summary className="min-h-11 cursor-pointer py-3 font-semibold">
									Assistenza e dettagli tecnici
								</summary>
								<ActionDetails actions={group.actions} ownerId={ownerId} technical />
								<button
									type="button"
									className={ACTION_CLASS}
									onClick={() => downloadSummary(group, `timbrature-archiviate-${group.id}.json`)}
								>
									Scarica gruppo archiviato
								</button>
							</details>
						</div>
					))}
				</details>
			)}
		</>
	);
}
